import ErrorComponent from "@/components/ui/error";
import { reloadPageAfterUnexpectedError } from "@/types/session-storage";
import * as Sentry from "@sentry/react";
import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4">
          <div className="max-w-md">
            <ErrorComponent
              title="Unexpected Error"
              message={
                "An unexpected error occurred. Please try again later. If the error persists, please contact support."
              }
              action={{
                label: "Refresh Page",
                onClick: () => {
                  this.setState({ hasError: false, error: null });
                  reloadPageAfterUnexpectedError();
                },
              }}
            />
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Wrap our error boundary with Sentry's error boundary
export default Sentry.withErrorBoundary(ErrorBoundary, {
  fallback: (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <div className="max-w-md">
        <ErrorComponent
          title="Unexpected Error"
          message={
            "An unexpected error occurred. Please try again later. If the error persists, please contact support."
          }
          action={{
            label: "Refresh Page",
            onClick: reloadPageAfterUnexpectedError,
          }}
        />
      </div>
    </div>
  ),
  showDialog: false,
});
